import { useCallback, useEffect } from 'react'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { PreviousTaxPayments } from '@/features/taxes/taxes-profile/form-flow/PreviousTaxPayments'
import { fetchSafeHarborCatchUp } from '@/features/taxes/quarterly/safeHarborCatchUp.slice'
import { selectUnansweredPreviousFederalEstimates } from '@/features/taxes/quarterly/userTaxEstimates.selector'
import { QuarterlyTaxEstimateDetail } from '@/features/admin/quarterly-tax-estimate-details/quarterlyTaxEstimateDetails.slice'

import {
  CALCULATE_SAFE_HARBOR_STEP,
  useCalculateSafeHarborSteps,
} from './helpers'
import { CalculateSafeHarborFooter } from './CalculateSafeHarborFooter'

export const PriorQuarterPayments = ({
  details,
}: {
  details?: QuarterlyTaxEstimateDetail | null
}) => {
  const dispatch = useAppDispatch()
  // Zero, not one: it is the quarter number with nothing before it, which is
  // what this step should list if the details ever fail to load.
  const targetQuarter = Number(details?.taxQuarter ?? 0)
  const taxYear = Number(details?.taxYear ?? new Date().getFullYear())

  const { goToPreviousStep, goToNextStep } = useCalculateSafeHarborSteps(
    CALCULATE_SAFE_HARBOR_STEP.priorQuarterPayments,
    details?.taxQuarter
  )

  const unanswered = useReselector(
    selectUnansweredPreviousFederalEstimates,
    taxYear,
    targetQuarter
  )

  const refreshTotal = useCallback(() => {
    dispatch(fetchSafeHarborCatchUp())
  }, [dispatch])

  useEffect(() => {
    refreshTotal()
  }, [refreshTotal])

  return (
    <div className="flex flex-col gap-6 max-w-2xl mx-auto mt-10">
      <PreviousTaxPayments
        taxYear={taxYear}
        taxQuarter={targetQuarter}
        heading="Did you pay estimated taxes earlier this year?"
        note="Please update each quarter, even if you didn't make any payments. We need this to give you an accurate payment amount."
        explainer="We add up what you still owe for the quarters that have already passed, then subtract anything you've already paid. Without this we'd show you one quarter's worth, which would leave you under-paid."
        federalOnly
        onPaymentSaved={refreshTotal}
      />

      <CalculateSafeHarborFooter
        submitOrContinue={() => goToNextStep()}
        goToPreviousStep={goToPreviousStep}
        continueDisabled={unanswered.length > 0}
      />
    </div>
  )
}
